import { useEffect, useRef, useCallback } from 'react';
import BrokerInquiryForm from '../../broker-services/components/BrokerInquiryForm';

interface FindMeOneModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: {
    make: string;
    model: string;
    year: number;
    bodyType: string;
  } | null;
}

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const TITLE_ID = 'find-me-one-modal-title';

export default function FindMeOneModal({ isOpen, onClose, vehicle }: FindMeOneModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Body scroll lock + focus management
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      previousFocusRef.current = document.activeElement as HTMLElement;
      const raf = requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Focus trap + Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS) ?? []
    ).filter(el => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen || !vehicle) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — decorative, aria-hidden */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto z-10"
      >
        {/* Close Button */}
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-xl text-gray-700" aria-hidden="true"></i>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-[#1C2B1E] to-[#2d4430] px-8 py-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-[#E8B84B] rounded-full mx-auto mb-4">
            <i className="ri-search-eye-line text-[#1C2B1E] text-3xl" aria-hidden="true"></i>
          </div>
          <h2 id={TITLE_ID} className="font-['Oswald'] text-3xl font-bold text-white mb-2">
            Find Me One Like This
          </h2>
          <p className="text-white/80 text-base">
            We'll search nationwide for a {vehicle.year} {vehicle.make} {vehicle.model} or similar vehicle
          </p>
        </div>

        {/* Form */}
        <div className="p-6">
          <BrokerInquiryForm
            defaultMake={vehicle.make}
            defaultModel={vehicle.model}
          />
        </div>
      </div>
    </div>
  );
}
